import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemText, ListItemIcon, Typography, Grid, Paper, IconButton, useMediaQuery } from '@mui/material';

// Import images
import introImg from '../../../assets/images/intro_user_manual.png';
import orderflowImg from '../../../assets/images/manual/orderflow.jpg';
import marketProfileImg from '../../../assets/images/manual/market-profile.jpg';
import mpOldImg from '../../../assets/images/mp-chart.png';
import optTableImg from '../../../assets/images/manual/options-table.jpg';
import smartCCImg from '../../../assets/images/manual/smart-candlesticks.jpg';
import spectrumImg from '../../../assets/images/manual/spectrum.jpg';
import ntmVolxImg from '../../../assets/images/manual/ntm-volx.jpg';
import ntmOIImg from '../../../assets/images/manual/ntm-oi.jpg';
import gammaImg from '../../../assets/images/manual/gamma-plot.jpg';
import wcashImg from '../../../assets/images/manual/wcash-charts.jpg';
import mpOfDualImg from '../../../assets/images/manual/mp_of.jpg';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CandlestickChartOutlinedIcon from '@mui/icons-material/CandlestickChartOutlined';
import WaterfallChartOutlinedIcon from '@mui/icons-material/WaterfallChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import FiberNewIcon from '@mui/icons-material/FolderSpecial';
import PremiumAccessIcon from '@mui/icons-material/WorkspacePremium';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Index data structure
const MANUAL_SECTIONS = [
  {
    title: 'Start Here',
    subs: [
      {
        title: '<h2>Welcome to Vtrender Charts</h2>',
        description: `
        <p>Your all-in-one space to read market structure, real-time intent, and trader positioning using <strong>MarketProfile</strong>, <strong>Orderflow</strong>, and <strong>Options data</strong>. 
        This platform is designed to show you what's <em>really</em> happening behind every price move.</p>
        \n<p>This isn't about blind charting or hunting patterns that stopped working years ago.</p>
        \n<p>This is about reading the market's <em>structure</em>, its <em>real-time intent</em>, and <em>trader positioning</em> — all in one integrated, dynamic view.</p>
        \n<p>We created this manual to guide you clearly, visually, and confidently. And for your trading to improve.</p>`
      },
      {
        title: '<h2>Why MarketProfile + Orderflow + Options Together?</h2>',
        description: `<p>
            Most platforms show you candles. We show you <em>what’s behind the candles</em>.
          </p>

          <p>Here’s how the trio works together:</p>

          <ul>
            <li>
              <strong>MarketProfile</strong> reveals how the market is structured — who’s dominating
              value, what price zones are getting accepted, and where rejection is likely.

              <img src="${marketProfileImg}" alt="Market Profile" />
            </li>
            <li>
              <strong>Orderflow</strong> exposes real-time supply and demand — buying/selling
              imbalances, initiative trades, absorption, and traps, bar-by-bar.

              <img src="${orderflowImg}" alt="Orderflow" />
            </li>
            <li>
              <strong>Options Data</strong> shows you where traders are betting, hedging, or
              defending positions — from straddle setups to gamma builds and writer
              activity.

              <img src="${optTableImg}" alt="Options Table" />
            </li>
          </ul>

          <p>
            On their own, each tells a part of the story.<br>
            Together, they reveal the full market <em>intent and opportunity.</em>
          </p>
        `
      },

      {
        title: `<h2>How to Use This Manual</h2>`,
                description: `<p>This manual is your personal flight manual for Vtrender Charts — structured
            so you can explore in three ways:</p>

          <ol>
            <li>
              <strong>New to this?</strong><br>
              Start with the <strong>5-Min Quickstarts</strong>. These guides are like a friend
              sitting next to you, showing exactly what to click and what it means.
            </li>
            <li>
              <strong>Been here before?</strong><br>
              Go to <strong>Explore the Tools</strong> to dive deeper into each chart category —
              MarketProfile, Orderflow, Options, and more.
            </li>
            <li>
              <strong>Trading actively?</strong><br>
              Head to <strong>Advanced Tools & Custom Views</strong> for the full arsenal —
              like Speedo, Bookmarks, and Gamma overlays.
            </li>
          </ol>

          <p>
            ✨ You can also use the <strong>search bar</strong> (coming soon) or scroll through
            each section using the collapsible menu on the left.<br>
            We’ve also added visuals, glossary terms, and pro tips throughout —
            because learning should feel empowering, not overwhelming.
          </p>`
      },
      {
        title: `<h2>First-Time User Setup Checklist</h2>`,
        description: `<p>Just landed? Here’s your no-fuss setup:</p>

          <ul>
            <li>Log in with your Vtrender ID</li>
            <li>Choose an instrument (e.g., NIFTY Futures) from the dropdown</li>
            <li>Load a MarketProfile chart or an Orderflow chart</li>
            <li>Split screen: Add Orderflow on the right</li>
            <li>Click ‘Options Table’ → Select NTM strikes</li>
            <li>Locate VWAP, POC, and IB range on the chart</li>
            <li>Try the ‘Date Picker’ or ‘Replay’ for deeper analysis</li>
            <li>Bookmark your layout for tomorrow</li>
          </ul>
        
          <p>
            You’re now up and running with the core of Vtrender’s ecosystem.<br>
            Each section in the manual will build on this foundation.
          </p>`
        
      },
      
    ],
  },
  {
    title: 'How to Use Charts',
    subs: [
      {
        title: `<h2> Step 1: Navigating the Dashboard</h2>`,
        description: `<p>
                    Start from the <strong>Hamburger menu</strong> at the top left.
                  </p>

                  <p>
                    A full menu opens on the left and you will see all the options here.
                    Start with <strong>Orderflow</strong>, <strong>MarketProfile</strong>, and <strong>Smart Candlesticks</strong>.
                  </p>

                  <p>
                    There is lots more below. Use the <strong>dropdown menu</strong> to open up more menu options.
                  </p>`
      },
      {
        title: `<h2>Step 2: Loading a Chart Layout</h2>`,
        description: `<p>
                      Start from the <strong>Hamburger menu</strong> at the top left.
                    </p>

                    <p>
                      A full menu opens on the left and you will see all the options here.
                      Start with <strong>Orderflow</strong>, <strong>MarketProfile</strong>, and <strong>Smart Candlesticks</strong>.
                    </p>

                    <p>
                      There is lots more below. Use the <strong>dropdown menu</strong> to open up more menu options.
                    </p>
                    `
      },
      {
        title: `<h2>Step 3: Using Zoom and Bookmarks & Saved Views</h2>`,
        description: `<p>
                      You can use the <strong>mouse scroll</strong> in Windows to zoom in/out,
                      or use the <strong>arrow keys</strong> (↑ / ↓) to scroll vertically through the chart.
                    </p>

                    <p>
                      <em>(iOS settings for zooming and navigation will be added here.)</em>
                    </p>

                    <p>
                      If the chart goes out of scale, use the <strong>Reset</strong> button located either:
                      <ul>
                        <li>Floating at the bottom center of the screen</li>
                        <li>Or at the extreme bottom-right corner of the chart</li>
                      </ul>
                    </p>
                    `
      },
      {
        title: `<h2>Step 4: Playback & Replay (Optional Tools)</h2>`,
        description: `<p>
                      Use the <strong>Date Picker</strong> at the top center to quickly go to any date in the past
                      and see how the market behaved on that day.
                    </p>

                    <p>
                      Use <strong>Replay Mode</strong> to review past market sessions at your own pace.
                      Pause, rewind, and rewatch how price moved, how Orderflow shifted, or how a breakout formed.
                    </p>

                    <p>
                      Ideal for <em>refining strategy</em>, <em>spotting missed signals</em>, or
                      <em>mentoring new traders</em>.
                    </p>
                    `
      },
    ],
  },



  {
    title: '5 Min Quick Start',
    subs: [
      {
        title: `<h2>Your First Orderflow Chart</h2>`,
        description: ` <p>Want to see who’s really buying or selling? Let’s open your first Orderflow chart.</p>

                      <ol>
                        <li>From the left menu, click <strong>Orderflow Charts</strong></li>
                        <li>Select an instrument (try <strong>NIFTY Futures</strong>)</li>
                        <li>Watch for colored blocks inside the candle – that’s live trade data</li>
                        <li>Hover to inspect volume, buyers, and sellers</li>
                      </ol>

                      <div class="tip">
                      💡 <strong>Look for purple bars</strong> — those are Initiative Sellers stepping in.<br>
                      💡 <strong>Green bars</strong> indicate Initiative Buyers.
                      </div>

                      <img src="${orderflowImg}" alt="Orderflow Charts" />

                    `
      },
      {
        title: `<h2>Reading a MarketProfile Chart</h2>`,
        description: ` <p>Let’s read the market’s structure — where it’s balanced, where it’s pushing.</p>

                      <ol>
                        <li>Click <strong>MarketProfile Charts</strong> from the menu</li>
                        <li>Choose <strong>BANKNIFTY Futures</strong> (or any active symbol)</li>
                        <li>Focus on:</li>
                      </ol>

                      <ul>
                        <li><strong>POC (Yellow Line)</strong> — the price with highest activity</li>
                        <li><strong>Value Area (Blue Blocks)</strong> — where 70% of trading happened</li>
                        <li><strong>IB Range</strong> — early market direction</li>
                      </ul>
                      <div class="tip">
                      💡 <strong>If price is above VAH and holding, buyers may be in control.</strong>
                      </div>

                      <img src="${marketProfileImg}" alt="Market Profile Charts" />
                    `
      },

     {
      title: `<h2>See Option Writers in Action</h2>`,
      description: ` <p>Let’s catch what option sellers are doing in real time:</p>

                      <ol>
                        <li>Open the <strong>Options Table</strong></li>
                        <li>Focus on strikes <strong>close to the spot price</strong></li>
                        <li>Compare <strong>Call OI vs Put OI</strong></li>
                        <li>Look for higher volume & OI on one side — that’s <strong>writing activity</strong></li>
                      </ol>

                      <div class="tip">
                        💡 <strong>Heavy Call writing = resistance. Heavy Put writing = support.</strong>
                      </div>

                      <img src="${optTableImg}" alt="Options Table" />
                  `
      },
      {
        title: `<h2>Bookmark a Setup</h2>`,
        description: ` <p>Don’t lose your perfect layout — save it for tomorrow in seconds:</p>

                      <ol>
                        <li>Load your preferred <strong>MarketProfile + Orderflow</strong> view</li>
                        <li>Click the <strong>Bookmarks</strong> button</li>
                        <li>Name it (e.g., “<strong>Morning View – NIFTY</strong>”)</li>
                        <li>Access it anytime from the <strong>Bookmarks Menu</strong></li>
                      </ol>

                      <div class="tip">
                        💡 <strong>Power tip:</strong> Save different layouts for <em>Opening Range</em> vs. <em>EOD replays</em>.
                      </div> 

                      <img src="${mpOfDualImg}" alt="Market Profile + Orderflow Charts" />
                      `
      },
      {
        title: `<h2>(Optional) Your First 3 Signals to Watch</h2>`,
        description: ` <p>Start spotting intent fast with these key cues:</p>

                      <div class="signal">
                        <strong>IB30 / IS30 Breakout →</strong> Price breaks above/below showing strong Buyers / Sellers or Big Money
                      </div>

                      <div class="signal">
                        <strong>COT Shift →</strong> A sudden flip in buyer/seller aggression
                      </div>

                      <div class="signal">
                        <strong>Trifecta Bar →</strong> A combo of buying/selling imbalances = strong signal
                      </div>`
      }
    ],
  },

  {
    title: 'Explore OrderFlow',
    subs: [
      {
        title: `<h2>Orderflow Basics</h2>`,
        description: ` <p>
                        Orderflow shows you <strong>what’s happening inside the candle</strong> — not just the price, 
                        but <strong>who’s trading</strong>, <strong>how much</strong>, and <strong>with what intent</strong>.
                      </p>

                      <p>Each bar contains a split between buyers and sellers. You’ll see:</p>

                      <ul>
                        <li>Volume traded at each price level</li>
                        <li>Whether trades hit the bid or the ask</li>
                        <li>Colored bars showing Initiative or Responsive action</li>
                      </ul>

                      <div class="tip">
                        💡 <strong>If price is rising but buyers are thinning out — that’s a trap.</strong>
                      </div>

                      <img src="${orderflowImg}" alt="Orderflow Chart" />

                      `
      },
      {
        title: `<h2>Orderflow Intro Video Series</h2>`,
        description: `
          <script src="https://player.vimeo.com/api/player.js"></script>
          
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 20px;">
            <!-- Row 1 -->
            <!-- Video 1 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853735125?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">Introduction to Vtrender's Orderflow Charts</h3>
              </div>
            </div>
            
            <!-- Video 2 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853796900?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">Market Liquidity in Orderflow</h3>
              </div>
            </div>
            
            <!-- Video 3 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853805750?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">COT and OI in Orderflow Charts</h3>
              </div>
            </div>
            
            <!-- Row 2 -->
            <!-- Video 4 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853812666?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">Market Dynamics with RB, IB, RS and IS</h3>
              </div>
            </div>
            
            <!-- Video 5 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853750725?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">Volume Clusters in Orderflow Charts</h3>
              </div>
            </div>
            
            <!-- Video 6 -->
            <div style="background-color: rgba(17, 50, 160, 0.3); border-radius: 8px; overflow: hidden; border: 1px solid rgba(63, 105, 248, 0.15); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column;">
              <div style="padding:56.25% 0 0 0;position:relative;">
                <iframe src="https://player.vimeo.com/video/853743768?h=c8c9f2c77f&title=0&byline=0&portrait=0" 
                  style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                  frameborder="0" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowfullscreen>
                </iframe>
              </div>
              <div style="background-color: rgba(17, 95, 98, 0.3); padding:12px 8px; display: flex; align-items: center; justify-content: center; flex-grow: 1;">
                <h3 style="margin:0; font-size:14px; text-align:center; color: #e0e6f5; font-weight: 700; line-height: 1.4;">Understanding Imbalances in Orderflow Charts</h3>
              </div>
            </div>
          </div>
        `
      },
      {
        title: `<h2>Using the Orderflow Chart</h2>`,
        description: ` <ol>
                        <li>Click <strong>Orderflow Charts</strong> from the left menu</li>
                        <li>Select your instrument (e.g., <strong>NIFTY Futures</strong>)</li>
                        <li>Read the bar like this:
                          <ul>
                            <li><strong>Green font numbers</strong> = buyers dominate</li>
                            <li><strong>Red font numbers</strong> = sellers dominate</li>
                            <li><strong>Purple bars</strong> = Initiative Selling (IS)</li>
                            <li><strong>Green bars</strong> = Initiative Buying (IB)</li>
                          </ul>
                        </li>
                      </ol>

                      <p>Hover to see exact volume. Use the split view (price in 5 vs price in 30 mins) for pressure analysis.</p>

                      <div class="tip">
                        💡 <strong>You don’t trade the bar — you trade what it reveals.</strong>
                      </div> 
                      `
      },
      {
        title: `<h2>What You See on the Orderflow Chart</h2>`,
        description: ` <p>Here’s what makes the Orderflow chart come alive:</p>

                      <ul>
                        <li><strong>Info Panel (bottom)</strong> → Volume, Max Vol of bar, COT, Open Interest — all in one place</li>
                        <li><strong>Global Volume (GV)</strong> → Tracks total volume on the chart as a Volume Profile</li>
                        <li><strong>10 Bar COT</strong> → Change in trade aggression (buying/selling shift)</li>
                      </ul>

                      <h3>Timeframes to Try:</h3>

                      <ul>
                        <li><strong>1-min</strong> → Scalping, speed analysis</li>
                        <li><strong>5-min</strong> → Intraday flow</li>
                        <li><strong>30-min</strong> → Bias + context</li>
                      </ul>

                      <div class="tip">
                        💡 <strong>Pro Tip:</strong> Try <strong>1-min</strong> for entry, <strong>5-min</strong> for confirmation, and <strong>30-min</strong> for bias.
                      </div> `
      }, 
      {
        title: `<h2>Key Orderflow Signals: IB, IS, and COT</h2>`,
        description: ` <p>We mark <strong>Initiative Buying (IB)</strong> and <strong>Initiative Selling (IS)</strong> using purple/green bars.</p>

                        <p>Here’s how to use them:</p>

                        <ul>
                          <li><strong>IB30</strong>: Buying which is initiative — strength</li>
                          <li><strong>IS30</strong>: Selling which is initiated/aggressive — aggressive players move markets</li>
                          <li><strong>COT Flip</strong>: Change in trade aggression — often precedes breakout</li>
                        </ul>

                        <div class="tip">
                          💡 <strong>Combine signals for stronger conviction</strong> — e.g., <strong>IS30 + COT flip = downside play</strong>.
                        </div> `
      },
      {
        title: `<h2>Advanced Tools: Speedo & Replay</h2>`,
        description: ` <p><strong>Speedo</strong> shows <em>pace-of-tape</em> — ideal for catching momentum bursts.<br>
                        <strong>Replay</strong> lets you revisit the day to understand market flow, volume shifts, and emotional triggers.</p>

                        <p><strong>How to use:</strong></p>

                        <ul>
                          <li>Click <strong>Replay Tool</strong> to load any day</li>
                          <li>Use <strong>Speedo</strong> during live session to sense pressure</li>
                        </ul>

                        <div class="tip">
                          💡 <strong>Traders love using Replay to build instinct.</strong> It’s the gym for your edge.
                        </div> `
      },
      {
        title: `<h2>LLTs, Illiquidity, and Divergence</h2>
`,
        description: ` <p> These advanced reads set apart reactive traders from the prepared ones:</p>

                      <ul>
                        <li><strong>LLT</strong> = Large Lot Traders — they either <em>start</em> a move or <em>finish</em> an existing one.</li>
                        <li><strong>Zero Print Bar</strong> = Signals <em>illiquidity</em> or lack of real volume. These are often excellent stop zones.</li>
                        <li><strong>COT Divergence</strong> = Price makes a new high, but COT turns <strong>red</strong> — the market is being sold into. Look for short setups.</li>
                      </ul>

                      <div class="tip">
                        💡 <strong>Look left, then right, and zoom in</strong> — you're not guessing anymore.<br>
                        You're watching the street and crossing with intent.
                      </div> `
      }

      
    ],
  },
  {
    title: 'Explore Marketprofile',
    subs: [
      {
        title: `<h2>What Is Market Profile?</h2>`,
        description: ` <p><strong>Market Profile</strong> is your <em>market structure blueprint</em> — it shows you where the market is building value, where it’s balanced, and where it’s likely to break.</p>

                      <p>Instead of just price and time, you now see:</p>

                      <ul>
                        <li>Where most trading happened (<strong>POC</strong>)</li>
                        <li>The zone that defines market acceptance (<strong>Value Area</strong>)</li>
                        <li>The early price range that sets the tone (<strong>Initial Balance</strong>)</li>
                        <li>Whether the market is consolidating or breaking structure</li>
                      </ul>

                      <div class="tip">
                        💡 <strong>When price moves outside structure, it tells a story.</strong><br>
                        And that story can be traded.
                      </div> 
                      
                      <img src="${mpOldImg}" alt="Market Profile Chart" />
                      `
      },
      {
        title: `<h2>Key Elements of Market Structure</h2>`,
        description: ` <p>These 3 elements are your <em>anchors</em> in a dynamic market:</p>

                      <h3> Point of Control (POC)</h3>
                      <p>The price where the most volume traded — the “fairest” price.<br>
                      If price stays above or below it, bias shifts.</p>

                      <h3> Value Area (VA)</h3>
                      <p>The zone where 70% of activity took place.<br>
                      <strong>VAH</strong> = Value Area High<br>
                      <strong>VAL</strong> = Value Area Low<br>
                      Price inside VA = balanced, chop.<br>
                      Price outside VA = potential shift or drive.</p>

                      <h3> Initial Balance (IB)</h3>
                      <p>The first hour’s range.<br>
                      Breakouts from IB = initiative control.<br>
                      Rejection from IB extremes = mean reversion.</p>

                      <div class="tip">
                        💡 <strong>Think of these as the walls, floor, and roof of the day’s structure.</strong>
                      </div>`
      },
      {
        title: `<h2>Reading Daily, Weekly & Composite Structures</h2>`,
        description: `
                    <p>You can view Market Profile in different timeframes to <em>zoom in or zoom out</em> of the market’s structure:</p>

                    <ul>
                      <li class="daily"><strong>Daily Profile</strong> → See each day’s auction, structure shifts</li>
                      <li class="weekly"><strong>Weekly/Monthly Profile</strong> → Observe long-term balance, buildup, and breakout zones</li>
                      <li class="composite"><strong>Composite Profile</strong> → Combine multiple sessions to find macro-level structure (big-picture POC & VA)</li>
                    </ul>

                    <div class="tip">
                      💡 <strong>Composites are like looking at the skeleton beneath the skin</strong> — they reveal hidden balance and control zones.
                    </div>`
      },
      {
        title: `<h2>Profile Shapes & Market Behaviour</h2>`,
        description: `
                    <p>Market Profile isn't just about lines — it's about <em>shape</em>, and each shape has a story:</p>

                    <ul>
                      <li class="d"><strong>D-shape</strong> → Balance. Price discovery complete. Expect range.</li>
                      <li class="b"><strong>b-shape</strong> → Longs trapped. Watch for breakdown.</li>
                      <li class="p"><strong>P-shape</strong> → Shorts trapped. Look for upside follow-through.</li>
                      <li class="trend"><strong>Trend Day</strong> → Directional conviction. Bias continues next day unless structure changes.</li>
                    </ul>

                    <div class="tip">
                      💡 <strong>Structure tells you the auction's emotional state</strong> — and that’s your edge.
                    </div>
                    `
      },

      {
        title: ` <h2>Your First Market Structure Read</h2>
`,
        description: `
                    <p>Here’s a simple checklist for reading a profile:</p>

                    <ul>
                        <li>Where is today’s POC vs yesterday’s?</li>
                        <li>Is price inside or outside the Value Area?</li>
                        <li>Has price extended beyond the Initial Balance?</li>
                        <li>What shape is the profile forming?</li>
                        <li>Is there a gap or rejection from prior structure?</li>
                      </ul>

                    <div class="tip">
                      💡 <strong>This isn’t just a chart.</strong> It’s a conversation between buyers and sellers — and you’re learning to listen in.
                    </div>

                    <p>Want to go deeper? Learn about <em>“Structure Breaks,” “Poor Highs/Lows,” and “Day Types”</em> in our <a href="#">Advanced Structure Module</a>.</p>

                    `
      },
      {
        title: '<h2>Bonus : Using Market Profile + Orderflow Together</h2>',
        description: `
                    <div class="bonus">
                        <p><strong>Combining Structure with Intent</strong> — use Market Profile and Orderflow <em>side by side</em> to see the <strong>full story</strong> of the market.</p>

                        <div class="panel-layout">
                          <p>Use the <strong>Two-Window Layout:</strong></p>
                          <ul>
                            <li><strong>Left Panel</strong> → Market Profile (MP) chart</li>
                            <li><strong>Right Panel</strong> → Orderflow (OF) chart</li>
                            <li>Same symbol, same timeframe (e.g., BANKNIFTY Futures, 5-min)</li>
                          </ul>
                        </div>
                    </div>
                    `
      }
    ],
  },
  {
    title: 'Smart Candlesticks',
    subs: [
      {
        title: '<h2>What Are Smart Candlesticks?</h2>',
        description: `
                      <p>These aren’t your typical red-and-green sticks.</p>

                      <p><strong>Vtrender’s Smart Candlesticks</strong> combine:</p>

                      <ul>
                        <li>Clean candle price action</li>
                        <li>Real-time volume pressure</li>
                        <li>Structural markers like IB ranges, POC, VWAP, and COT shifts</li>
                      </ul>

                      <p>You’re no longer staring at price alone — you're seeing <strong>context + structure + intent</strong> inside each bar.</p>

                      <div class="tip">
                        💡 Think of it as price with a mind of its own — but now, you can read it clearly.
                      </div>

                      <img src="${smartCCImg}" alt="Smart Candlesticks Chart" />
                      `
      },

    {
      title: ' <h2>What You’re Seeing on This Chart</h2>',
      description: `
                    <p><strong>Deconstructing the Chart View</strong></p>

                    <p>From your image, here’s what each part is telling us:</p>

                    <ul>
                      <li><strong>Candles:</strong> Traditional OHLC bars (Open, High, Low, Close)</li>
                      <li><strong>IB30 / IS30 Bars:</strong> Yellow and purple bars showing initiative buying/selling</li>
                      <li><strong>Colored Volume (Below Candles):</strong>
                        <ul>
                          <li><span style="color:#00FF00;">Green bars</span> = Positive intent</li>
                          <li><span style="color:#E64F12;">Red bars</span> = Negative intent</li>
                        </ul>
                      </li>
                      <li><strong>VWAP Line (Green):</strong> Tracks fair value as it evolves</li>
                      <li><strong>Initial Balance (IB):</strong> First hour’s high and low — a critical structure zone</li>
                      <li><strong>DPOC Marker:</strong> White line marking the day’s highest traded price (Point of Control)</li>
                      <li><strong>COT Value (Top-Left):</strong> Real-time shifts in trade aggression</li>
                      <li><strong>Volume Histogram (Bottom):</strong> Displays actual trade intensity by bar</li>
                    </ul>

                    <div class="tip">
                      💡 Every candle here is filtered through the lens of <em>intent</em> — it’s not just price movement, it’s motive.
                    </div>
                                      
                      `
    },
    {
      title: ' <h2>How to Read It Like a Pro</h2>',
      description: `
                        <p><em>This chart is built for real-time narrative reading. Here's how:</em></p>

                        <ul>
                          <li>
                            <strong>Start with the POC:</strong><br>
                            → Is price <strong>accepting</strong> it, <strong>rejecting</strong> it, or <strong>moving away</strong>?
                          </li>
                          <li>
                            <strong>Watch the VWAP:</strong><br>
                            → <span>Above VWAP</span> = Bullish bias<br>
                            → <span>Below VWAP</span> = Selling control
                          </li>
                          <li>
                            <strong>Look at the Volume + COT Panel:</strong><br>
                            → <span>Green bars on down candles</span> = Absorption (buyers stepping in)<br>
                            → <span>Red bars on up candles</span> = Trap (sellers absorbing demand)
                          </li>
                          <li>
                            <strong>Follow IB Bars – Breakouts or Holds:</strong><br>
                            → Price holding outside = Initiative control<br>
                            → Rejected move = Watch for stop-losses above/below IB or IS bars
                          </li>
                        </ul>

                        <div class="tip">
                          💡 Don’t trade the candle. Trade what the market is saying <em>through</em> the candle.
                        </div>
                    
                    `
    },
    {
      title: ' <h2>How to Use It in Live Trading</h2>',
      description: `
                      <h3>Putting It to Use in the Live Market</h3>
                        <p>This view is ideal for:</p>
                        <ul>
                        <li>Bias confirmation (VWAP + POC hold)</li>
                        <li>Breakout strength checks (IB + COT)</li>
                        <li>Identifying fake moves (Volume divergence)</li>
                      <li>Entry alignment (e.g., breakout + positive COT)</li>
                    </ul>

                    <p><strong>Pro traders in the VMR use this daily for:</strong></p>
                    <ul>
                    <li>Spotting real control vs noise</li>
                    <li>Identifying intent before volume spikes</li>
                    <li>Aligning setups with the overall structure</li>
                  </ul>

                    <div class="note">
                    💡 <strong>Note:</strong> You get the best of both worlds — candle intuition + structure clarity.
                  </div>

                    <div class="divider"></div>

                      <h3>🔧 How to Load the Smart Candlestick View</h3>
                      <p><strong>Step-by-Step:</strong></p>
                      <ul>
                      <li>From the left menu, click <strong>“Candle Line Chart”</strong></li>
                      <li>Select your instrument (e.g., <em>NIFTY Futures</em>)</li>
                      <li>From the Panel Controls at the bottom:</li>
                      <ul>
                        <li>Enable <strong>VWAP</strong></li>
                        <li>Enable <strong>Initial Balance (IB)</strong> lines</li>
                        <li>Toggle on <strong>POC</strong> and <strong>Value Area</strong> if needed</li>
                      </ul>
                      <li>Turn on the <strong>Volume COT Panel</strong> (to see trade intent)</li>
                    </ul>

                      <h3>✅ What You’ll See After Setup</h3>
                      <ul>
                      <li>Candlesticks with VWAP overlay</li>
                      <li>IB range plotted as reference lines</li>
                      <li>Live COT pressure below each candle</li>
                      <li>Optional: Open Interest display</li>
                      <li>Total volume histogram at the bottom</li>
                      <li>Top bar showing OHLC + COT values</li>
                    </ul>

                      <div class="divider"></div>

                      <h3>🧠 Pro Setup Tip</h3>
                      <p>Switch to a <strong>2-panel layout</strong> for deeper insight:</p>
                      <ul>
                      <li><strong>Left panel:</strong> Smart Candlestick view</li>
                      <li><strong>Right panel:</strong> Orderflow Chart (same instrument and timeframe)</li>
                    </ul>
                    <p>This setup gives you both <strong>price structure</strong> and <strong>trade execution flow</strong> in a single glance.</p>

                                      
                    `
    },
    ],
  },
  {
    title: 'Explore Options',
    subs: [
      {
        title: ' <h2>Understanding the Spectrum</h2>',
        description: `
        <h2>📈 Understanding the Spectrum – Mapping the Option Range</h2>

                      <p>The <strong>Spectrum Chart</strong> is your <em>visual range finder</em> — it shows where the market is likely to stay, expand, or reject based on open interest, implied volatility, and premium activity.</p>

                      <p><strong>Spectrum is built on key principles of options positioning:</strong></p>
                      <ul>
                      <li>How traders (especially writers) structure trades around the spot</li>
                      <li>Where the largest option open interests are stacked</li>
                      <li>Whether those zones are acting like walls (resistance/support) or elastic bands (expansion zones)</li>
                    </ul>

                      <div class="tip">
                      💡 Think of Spectrum as the weather radar for the market’s option structure — it tells you if the sky is clear, cloudy, or stormy.
                    </div>

                    <img src="${spectrumImg}" alt="Spectrum" />

                      <div class="section">
                      <h3>🧭 Where to Access This:</h3>
                      <ul>
                        <li>Click <strong>‘Spectrum Charts’</strong> from the left-hand menu</li>
                        <li>Choose your instrument (e.g., <em>NIFTY</em> or <em>BANKNIFTY</em>)</li>
                        <li>View the full-day option range plotted across strikes</li>
                      </ul>
                    </div>

                      <div class="section">
                      <h3>🔍 What You See on the Spectrum:</h3>
                      <ul>
                        <li><strong>Central Spot Range</strong> → Where current price sits</li>
                        <li><strong>Upper & Lower Boundaries</strong> → Near-the-money strikes with high OI & IV</li>
                        <li><strong>Shaded Zones</strong> → Zones of possible containment (neutral range)</li>
                        <li><strong>Fading Ends</strong> → Low OI/high IV zones prone to expansion (edge breakouts)</li>
                      </ul>
                    </div>

                      <div class="section">
                      <h3>✅ How to Use It in Trading:</h3>
                      <ul>
                        <li><strong>Use Spectrum before the day starts</strong> to understand where writers are likely to defend</li>
                        <li>Combine it with <strong>VIX and volatility expectations</strong> to size the range</li>
                        <li>If price is moving toward a boundary <strong>with rising volume + COT support</strong>, you may be looking at a range break</li>
                        <li>If price stalls at a Spectrum edge <strong>and Orderflow shows absorption</strong>, it may be a trap or fade setup</li>
                      </ul>
                    </div>

                      <div class="tip">
                      💡 Combine this with <strong>NTM Volume</strong> and <strong>Gamma</strong> for full picture control.
                    </div>
                    
                    `
      },
    
      {
        title: '<h2>Using NTM VolX – Spotting Real-Time Directional Bias</h2>',
        description: `
        <h3><strong>NTM VolX – Reading Buyer vs Seller Action at the Frontline</strong></h3>

                      <p>
                      The <strong>NTM VolX</strong> chart shows you <strong>how much call and put volume is building around the spot</strong> — near-the-money strikes where the battle is most intense.
                    </p>

                      <p><strong><em>“Where is the real activity happening right now?”</em></strong></p>

                      <p>This is especially useful when you want to:</p>
                      <ul>
                        <li>Confirm breakout potential</li>
                        <li>Identify whether a move has <strong>volume support</strong> or not</li>
                        <li>Spot fading momentum or reversals in early stages</li>
                      </ul>

                      <div class="tip">
                      💡 <strong>Volume reveals urgency. And urgent traders are usually positioned near the money.</strong>
                      </div>

                      <img src="${ntmVolxImg}" alt="NTM VolX Chart" />

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <ul>
                        <li>Click <strong>‘NTM VolX Charts’</strong> from the left-hand menu</li>
                        <li>Choose <strong>NIFTY</strong> or <strong>BANKNIFTY</strong></li>
                        <li>View the latest Call vs Put volumes at each NTM strike</li>
                      </ul>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li><strong>Spot Marker</strong> → Shows where the index is trading</li>
                        <li><strong>Call Bars (Blue)</strong> → Total call volume per strike</li>
                        <li><strong>Put Bars (Purple)</strong> → Total put volume per strike</li>
                        <li><strong>Bar Color Intensity</strong> → Indicates growing or slowing volume</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li>If <strong>Call volume dominates</strong> near spot → traders expect price to stay <strong>capped</strong></li>
                        <li>If <strong>Put volume dominates</strong> → bearish sentiment is <strong>building</strong></li>
                        <li>If <strong>both sides are high</strong> → expect a <strong>range or volatile day</strong></li>
                      </ul>

                      <div class="tip">
                      💡 <strong>Don’t just watch price. Watch who’s getting in the ring to trade at that price.</strong>
                      </div>
        
                    `
      },
      {
        title: '<h2>NTM OI Chart – Tracking Commitment Near the Price</h2>',
        description: `
                      <h3><em>NTM OI – The Options Market's Line in the Sand</em></h3>

                      <p>If <strong>Volume</strong> tells you <em>what's happening now</em>, then <strong>Open Interest (OI)</strong> tells you <em>who’s staying in the game</em>.</p>
                      <p>The <strong>NTM OI Chart</strong> shows where traders are <em>building or reducing positions</em> around the spot price — it helps you understand what the <strong>market is defending or giving up</strong>.</p>

                      <div class="note">💡 <em>This is not just sentiment. It's commitment. And commitment is what shapes structure.</em>
                      </div>

                      <img src="${ntmOIImg}" alt="NTM OI Chart" />

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <ul>
                        <li>Click <strong>‘NTM OI Change Charts’</strong> in the left-hand menu</li>
                        <li>Choose your instrument (NIFTY / BANKNIFTY)</li>
                        <li>View the latest <strong>build-up or unwinding</strong> in Calls and Puts near the spot</li>
                      </ul>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li><strong>Center Marker</strong> = Current spot</li>
                        <li><strong>Call OI Bars</strong> (Blue)</li>
                        <li><strong>Put OI Bars</strong> (Purple)</li>
                        <li><strong>Color/Highlight</strong> = Shows buildup or reduction</li>
                      </ul>
                      <div class="note"><em>Chart is updated every 3 minutes</em></div>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li>If <strong>OI is increasing at a strike</strong>, that strike becomes more important — possibly a wall</li>
                        <li>If <strong>OI unwinds during a move</strong>, it could signal:
                          <ul>
                            <li>Traders exiting = potential breakout</li>
                            <li>Or a trap = watch Orderflow for confirmation</li>
                          </ul>
                        </li>
                        <li>Pair it with <strong>NTM Volume</strong>:
                          <ul>
                            <li><strong>High Volume + High OI</strong> = Fresh aggressive build</li>
                            <li><strong>High Volume + Dropping OI</strong> = Exit = possible reversal zone</li>
                          </ul>
                        </li>
                      </ul>

                      <p>💡 <em>You don’t want to go long into a wall of fresh Call OI. And you don’t want to short where Puts are getting added fast.</em></p>
                      `
      },

      {
        title: '<h2>Gamma Exposure – Understanding the Market’s Invisible Grip</h2>',
        description: `
                     
                     <h3><em>Gamma Exposure – Where Price Gets Pulled or Repelled</em></h3>

                      <p>Gamma is the <strong>rate of change of delta</strong>, but you don’t need to be a quant to use it.</p>
                      <p>What matters: <strong>Gamma Exposure</strong> shows how <em>dealers are likely to behave</em> as price moves — whether they’ll <strong>hedge with or against</strong> the direction.</p>
                      <p>That behavior creates <strong>sticky zones, accelerations, and stalls</strong>. It’s invisible to most, but with this chart — you see it clearly.</p>

                      <p>💡 <em>Gamma doesn’t predict price. It predicts dealer behavior — and dealers move markets.</em></p>

                      <img src="${gammaImg}" alt="Gamma Chart" />

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <ul>
                        <li>Click <strong>‘Gamma Charts’</strong> from the left-hand menu</li>
                        <li>Choose <strong>NIFTY</strong> or <strong>BANKNIFTY</strong></li>
                        <li>View Gamma Exposure by strike, net positive/negative zones, and spot location</li>
                      </ul>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li>📍 <strong>Spot Marker</strong> – Where price is right now</li>
                        <li>📊 <strong>Positive Gamma Zones</strong> – Dealer behavior is hedging <em>against</em> movement = stability</li>
                        <li>🔻 <strong>Negative Gamma Zones</strong> – Dealer behavior hedges <em>with</em> movement = increased volatility</li>
                        <li>📈 <strong>Bar Strength</strong> – Size of Gamma exposure per strike</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>Price in Positive Gamma Zone?</strong>
                          <ul>
                            <li>→ Expect stability, chop, mean-reverting price behavior</li>
                            <li>→ Breakouts tend to fail unless fueled by external flow</li>
                          </ul>
                        </li>
                        <li><strong>Price in Negative Gamma Zone?</strong>
                          <ul>
                            <li>→ Expect larger swings, directional follow-through</li>
                            <li>→ Dealers are hedging with the move = more fuel on the fire</li>
                          </ul>
                        </li>
                        <li><strong>Watch for Spot near zero Gamma (Gamma Flip)</strong>
                          <ul>
                            <li>→ This is the transition zone — often acts like a <em>price magnet</em> or slingshot zone</li>
                          </ul>
                        </li>
                      </ul>

                      <p>💡 <em>Want to know if that breakout has fuel or friction? Check the Gamma first.</em></p>
                            
                      `
      },

      {
        title: '<h2>Writer’s Cash – Following the Money Flow in Options</h2>',
        description: `
                      <h3><em>Writer’s Cache – Where Option Sellers Are Banking or Bleeding</em></h3>

                        <p>The <strong>Writer’s Cash</strong> chart shows you how much <strong>cash flow option writers are exposed to</strong>, strike by strike.</p>

                        <p>It tracks the <strong>unrealized value of premiums</strong> sitting on both sides of the market — Calls and Puts — and highlights where writers:</p>
                        <ul>
                          <li>Are accumulating the most premium</li>
                          <li>Could face maximum pain if price breaks certain zones</li>
                          <li>Are likely to defend, trap, or reverse price</li>
                        </ul>

                        <p>💡 <em>This is the options market’s money map — follow it, and you follow intent.</em></p>

                        <img src="${wcashImg}" alt="WCash Chart" />

                        <hr>

                        <h4>🧭 Where to Access This:</h4>
                        <ul>
                          <li>Click <strong>‘Writer’s Cashflow’</strong> from the left-hand menu</li>
                          <li>Select your index (e.g., <strong>NIFTY</strong> or <strong>BANKNIFTY</strong>)</li>
                          <li>View strike-wise exposure of Call and Put writers in ₹ value</li>
                        </ul>

                        <hr>

                        <h4>🔍 What You See on the Chart:</h4>
                        <ul>
                          <li>📈 <strong>Bar Graph of Cash Value per Strike</strong></li>
                          <li>🟠 <strong>Call Side Premiums</strong> – Represent resistance zones</li>
                          <li>🟢 <strong>Put Side Premiums</strong> – Represent support zones</li>
                          <li>🔁 <strong>Shifts in Peaks</strong> – Suggest change in defense zones / expectations</li>
                          <li>🧮 <strong>Calculated from:</strong> Option Price × OI × Lot Size</li>
                        </ul>

                        <hr>

                        <h4>✅ How to Use It in Trading:</h4>
                        <ul>
                          <li>Look for <strong>strikes with the highest writer premium</strong> → Writers will likely defend these zones</li>
                          <li>If <strong>price moves sharply into these zones</strong>, it creates <em>pain for writers</em> = higher chance of fast moves or exits</li>
                          <li>If <strong>price stalls at a high-premium strike</strong>, it may be a writer trap — confirmed using Orderflow</li>
                          <li>Use in conjunction with:
                            <ul>
                              <li><strong>Gamma Flip</strong>: Align premium zones with volatility expectations</li>
                              <li><strong>NTM OI & Volume</strong>: Confirm if writers are holding or exiting</li>
                              <li><strong>COT Data</strong>: See if buyers are pressing into those traps</li>
                            </ul>
                          </li>
                        </ul>

                        <p>💡 <em>This chart tells you: Who has skin in the game? How much? And where will it hurt the most?</em></p>
                              
                      `
      }
    ],
  },
  // Premium Acess
  {
    title: 'Premium Access',
    subs: [
      {
        title: `<h2>Total Open Interest – The Market’s Positioning Footprint</h2>`,
        description: `
                      <h3><em>Total OI – Where the Market Is Committed</em></h3>

                      <p>
                        The <strong>Total OI chart</strong> gives you a bird’s eye view of where traders are most committed — across all strikes, both Calls and Puts.
                      </p>

                      <p>
                        This isn’t just near-the-money noise. This is <strong>the entire battlefield</strong>.
                        It shows where positions are being built, unwound, defended, or ignored — across the entire options chain.
                      </p>

                      <p>💡 <em>Total OI reveals the backbone of market structure — not just where price is now, but where exposure is stacked.</em></p>

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <p>Click <strong>‘Total OI’</strong> from the left-hand menu under <strong>Premium Access</strong></p>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li>🔍 <strong>Spot Marker</strong> = current index level</li>
                        <li>📊 <strong>Call OI Bars</strong> (Right side) – commitment at resistance zones</li>
                        <li>📊 <strong>Put OI Bars</strong> (Left side) – commitment at support zones</li>
                        <li>📈 <strong>Volume overlays</strong> – highlight recent activity on top of older positioning</li>
                        <li>🔁 <strong>Chart updates with rolling OI snapshots</strong> – stay current</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>Strikes with high Call OI</strong> → potential supply/resistance</li>
                        <li><strong>Strikes with high Put OI</strong> → potential demand/support</li>
                        <li><strong>Watch for fresh OI builds</strong> → they tell you what’s being defended <em>today</em></li>
                        <li><strong>Watch for shrinking OI</strong> → that zone is being given up or unwound</li>
                        <li><strong>Combine with Volume and IV Curve</strong> for sharper context</li>
                      </ul>

                      <p>💡 <em>Example: If price is near a strike with massive OI on both sides — expect compression, not expansion.</em></p>
                      `
      },

      {
        title: '<h2>Total Volume – Reading the Market’s Loudest Signals</h2>',
        description: `
                      <h3><em>Total Volume – Who’s Active and Where Right Now</em></h3>

                      <p>
                        The <strong>Total Volume chart</strong> shows where <em>traders are stepping in today</em>, across all strikes.
                        This isn’t about legacy positioning — this is <strong>fresh activity</strong>, flowing in <em>right now</em>.
                      </p>

                      <p>
                        It’s the best real-time read on <strong>where the action is</strong> — and whether that action is call-heavy, put-heavy, or balanced.
                      </p>

                      <p>💡 <em>Volume shows urgency. When a strike lights up, the market is voting with its trades — not just opinions.</em></p>

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <p>Click <strong>‘Total Volumes’</strong> from the left-hand menu under <strong>Premium Access</strong></p>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li>🔍 <strong>Spot Marker</strong> = current index level</li>
                        <li>🔷 <strong>Call Volume Bars</strong> (Right side) – buying/selling pressure in calls</li>
                        <li>🔶 <strong>Put Volume Bars</strong> (Left side) – pressure on the downside</li>
                        <li>💼 <strong>Bar Height</strong> = Total number of contracts traded at that strike</li>
                        <li>📊 <strong>Live View</strong> – keeps building through the session</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>High Call volume above spot</strong> = resistance building or breakout setup</li>
                        <li><strong>High Put volume below spot</strong> = support or breakout fade</li>
                        <li><strong>Both Calls and Puts heavy around the same strike</strong> → watch for straddle logic or range play</li>
                        <li><strong>Use in conjunction with Total OI:</strong>
                          <ul>
                            <li><strong>High Volume + Low OI</strong> = Fresh build</li>
                            <li><strong>High Volume + High OI</strong> = Intense defense</li>
                            <li><strong>High Volume + Shrinking OI</strong> = Exit or trap</li>
                          </ul>
                        </li>
                      </ul>

                      <p>💡 <em>A silent chart is a passive market. A loud chart? That’s where opportunity is waking up.</em></p>
                                          `
      },

      {
        title: `<h2>IV Curve – Seeing Risk Where Others See Strikes</h2>`,
        description: `
                      <h3><em>IV Curve – How the Market Prices Fear and Opportunity</em></h3>

                      <p>
                        The <strong>Implied Volatility (IV) Curve</strong> shows the market’s <em>expectation of future movement</em> — not in price, but in <strong>premium</strong>.
                      </p>

                      <p>
                        Each strike has its own IV, and the shape of the curve reveals:
                      </p>
                      <ul>
                        <li>Where risk is priced in</li>
                        <li>Where traders are scared</li>
                        <li>Where the market is <em>comfortable staying</em></li>
                      </ul>

                      <p>💡 <em>This is the options market’s emotion meter — shaped by what traders fear, chase, or ignore.</em></p>

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <p>Click <strong>‘IV Curve’</strong> under <strong>Premium Access</strong><br>Choose your instrument (NIFTY / BANKNIFTY)</p>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li>🔍 <strong>X-Axis</strong> = Strike prices</li>
                        <li>📈 <strong>Y-Axis</strong> = Implied Volatility at each strike</li>
                        <li>💫 <strong>The Curve</strong> = Often forms a “smile” or skew based on where risk is priced</li>
                        <li>🌠 <strong>Spikes</strong> = Where traders expect large movement or impact</li>
                        <li>📊 <strong>Real-time updating</strong> based on live option pricing</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>Flat curve</strong> = low fear, rangebound expectations</li>
                        <li><strong>Steep skew</strong> (higher IV on one side) = market expects risk on that side</li>
                        <li><strong>Spikes at specific strikes</strong> = likely event pricing or institutional plays</li>
                        <li>
                          <strong>Compare IV to Historical Volatility (HV):</strong>
                          <ul>
                            <li><strong>If IV ≫ HV</strong> = market expects something big</li>
                            <li><strong>If IV ≪ HV</strong> = market may be underpricing risk</li>
                          </ul>
                        </li>
                      </ul>

                      <p>💡 <em>Use the IV Curve to check if you're paying too much for options — or if the other side is about to.</em></p>
                            
                      `
      },

      {
        title: `<h2>OI Visualization (OI Viz) – Mapping the Market’s Pressure Zones</h2>`,
        description: `
                      <h3><em>OI Viz – A Visual Map of Where the Market Is Bracing</em></h3>

                      <p>
                        <strong>OI Viz</strong> gives you a <em>strike-by-strike visual map</em> of Open Interest —
                        showing which levels have the strongest build-up and where the market may get pinned,
                        rejected, or stretched.
                      </p>

                      <p>
                        Unlike total OI which gives you raw values, <strong>OI Viz lets you see the battlefield</strong>.
                        Support. Resistance. Boundaries. Range compression. All of it — plotted clearly.
                      </p>

                      <p>💡 <em>It's not just data. It’s structure, seen from above.</em></p>

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <p>Click <strong>‘OI Viz’</strong> under <strong>Premium Access</strong><br>
                        Choose your instrument (NIFTY / BANKNIFTY)</p>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <ul>
                        <li>🔍 <strong>Strike-wise bar layout</strong> (centered around spot)</li>
                        <li>🔷 <strong>Right Side</strong> = Call OI</li>
                        <li>🔶 <strong>Left Side</strong> = Put OI</li>
                        <li>🔍 <strong>Tall bars</strong> = Strong positioning</li>
                        <li>⚠️ <strong>Visual compression zones</strong> = High OI clustering</li>
                        <li>🔁 <strong>Live update</strong> (with OI change if enabled)</li>
                      </ul>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li>Identify <strong>strong support and resistance</strong> zones — where OI clusters thick</li>
                        <li>Look for <strong>asymmetry</strong> in OI — one side dominating = directional bias</li>
                        <li>Combine with <strong>Volume and IV Curve</strong> to judge price behavior around that strike</li>
                        <li>Watch for <strong>sudden gaps or additions</strong> = positioning shifts</li>
                      </ul>

                      <p>💡 <em>OI Viz is the options market’s terrain map — know the hills before you charge.</em></p>

                                          
        `
      },

      {
        title: `<h2>Historical Volatility (HV) – Looking Back to Prepare Forward</h2>`,
        description: `
                      <h3><em>HV – What the Market Has Done, Not Just What It Thinks</em></h3>

                        <p>
                          While IV tells you what the market expects, <strong>Historical Volatility (HV)</strong>
                          tells you what the market actually <em>did</em>.
                        </p>

                        <p>
                          It’s the <strong>true behavior</strong> of price movement, calculated from real price
                          changes over time. HV gives you a benchmark:
                        </p>

                        <blockquote><strong>Is the market living up to its expectations or staying calm beneath the noise?</strong></blockquote>

                        <p>💡 <em>IV is opinion. HV is reality. And the gap between the two creates opportunity.</em></p>

                        <hr>

                        <h4>🧭 Where to Access This:</h4>
                        <p>
                          Click <strong>‘Historical Volatility (HV)’</strong> from the <strong>Premium Access</strong> menu<br>
                          Choose timeframe: 10-day, 20-day, 30-day, or 60-day window
                        </p>

                        <hr>

                        <h4>🔍 What You See on the Chart:</h4>
                        <ul>
                          <li>📉 <strong>HV plotted over time</strong> – rolling volatility measure</li>
                          <li>📊 Optional overlays of <strong>IV for comparison</strong></li>
                          <li>🔁 <strong>Trend lines or spikes</strong> during major events</li>
                          <li>🎯 Used to assess if premium is <strong>expensive or cheap</strong></li>
                        </ul>

                        <hr>

                        <h4>✅ How to Use It in Trading:</h4>
                        <ul>
                          <li>Compare <strong>IV vs HV</strong>:
                            <ul>
                              <li><strong>IV &gt; HV</strong> → Options are overpriced → good for <strong>selling</strong></li>
                              <li><strong>HV &gt; IV</strong> → Market is underpricing risk → good for <strong>buying</strong></li>
                            </ul>
                          </li>
                          <li><strong>Use HV trend</strong> to gauge market tone:
                            <ul>
                              <li>Rising HV → chaotic or expanding ranges</li>
                              <li>Falling HV → stable regime, likely mean-reverting setups</li>
                            </ul>
                          </li>
                          <li>Combine HV with <strong>Gamma and Total OI</strong> to see if market is about to get trapped or take off</li>
                        </ul>

                        <p>💡 <em>When the market's past is quiet but the premiums are expensive — someone’s paying up for insurance. Ask why.</em></p>

        `
      },

      {
        title: `<h2>Options Table – Strike-by-Strike Market Intelligence</h2>`,
        description: `
                      <h3><em>Options Table – The Entire Options Chain at Your Fingertips</em></h3>

                        <p>
                          The <strong>Options Table</strong> gives you a full-screen view of every strike — layered
                          with real-time data for:
                        </p>

                        <ul>
                          <li>Open Interest (OI)</li>
                          <li>Volume</li>
                          <li>Implied Volatility (IV)</li>
                          <li>Greeks (Delta, Gamma, Theta, Vega)</li>
                          <li>Premium prices</li>
                        </ul>

                        <p>This is the <strong>nerve center</strong> for traders who want to:</p>
                        <ul>
                          <li>Scan for opportunity</li>
                          <li>Spot positioning pressure</li>
                          <li>Analyze risk pricing</li>
                        </ul>

                        <p>💡 <em>It’s not just a table — it’s a live x-ray of market positioning and expectation.</em></p>

                        <hr>

                        <h4>🔍 What You See in the Table:</h4>
                        <table border="1" cellpadding="6" cellspacing="0">
                          <thead>
                            <tr>
                              <th>📘 Column</th>
                              <th>What It Tells You</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr><td>🧮 Strike Price</td><td>Base reference point</td></tr>
                            <tr><td>📊 OI & Volume</td><td>Current positioning and activity</td></tr>
                            <tr><td>⚙️ IV</td><td>Risk being priced at each strike</td></tr>
                            <tr><td>🧲 Delta</td><td>Sensitivity to underlying movement</td></tr>
                            <tr><td>🔥 Gamma</td><td>Hedge reaction to movement</td></tr>
                            <tr><td>🧘 Theta</td><td>Time decay impact</td></tr>
                            <tr><td>🌊 Vega</td><td>Sensitivity to IV change</td></tr>
                            <tr><td>💰 LTP (Call & Put)</td><td>Premium price right now</td></tr>
                          </tbody>
                        </table>

                        <p><strong>Filter or sort</strong> the table by OI, Volume, Delta, etc. for clearer insight.</p>

                        <hr>

                        <h4>✅ How to Use It in Trading:</h4>
                        <ul>
                          <li><strong>Find clusters</strong> of OI and high IV → high-pressure zones</li>
                          <li><strong>Sort by Delta ≈ 0.5</strong> to spot at-the-money activity</li>
                          <li><strong>Look for unusual Greeks</strong> (e.g., high Gamma + low IV) → fast-move potential</li>
                          <li>Combine with:
                            <ul>
                              <li><strong>Writer’s Cache</strong> to understand premium risk</li>
                              <li><strong>NTM Volume/OI</strong> to validate where action is peaking</li>
                              <li><strong>IV Curve</strong> to contextualize expensive vs cheap strikes</li>
                            </ul>
                          </li>
                        </ul>

                        <p>💡 <em>Use it for setups, hedges, traps, and exits — this is where smart money watches the board.</em></p>

                      
        `
      },

      {
        title: `<h2>Participant-Wise Positions – See Who’s Behind the Moves</h2>`,
        description: `
                      <h3><em>Participant-Wise Positions – Decode Institutional & Retail Sentiment</em></h3>

                      <p>
                        This tool gives you a breakdown of who’s doing what in the F&O market —
                        segmented by:
                      </p>

                      <ul>
                        <li><strong>FIIs</strong> – Foreign Institutional Investors</li>
                        <li><strong>DIIs</strong> – Domestic Institutions</li>
                        <li><strong>PROs</strong> – Brokers & prop desks</li>
                        <li><strong>Clients</strong> – Retail + HNIs</li>
                      </ul>

                      <p>
                        Each participant group plays differently. This view helps you
                        <strong>detect smart money behavior</strong>, <strong>herd traps</strong>, and
                        <strong>institutional shifts</strong> before the broader market reacts.
                      </p>

                      <p>💡 <em>Want to know if that breakout is real? Check who’s behind it.</em></p>

                      <hr>

                      <h4>🧑‍💼 Where to Access This:</h4>
                      <p>
                        Click <strong>‘Participant-Wise Positions’</strong> from the Premium Access menu
                      </p>

                      <hr>

                      <h4>🔍 What You See on the Chart:</h4>
                      <table border="1" cellpadding="6" cellspacing="0">
                        <thead>
                          <tr>
                            <th>📘 Metric</th>
                            <th>Insight</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>📈 Long/Short Futures</td><td>Directional conviction by each group</td></tr>
                          <tr><td>📊 Options Long/Short</td><td>Strategic bias: hedging vs speculation</td></tr>
                          <tr><td>🔁 Net Change</td><td>Positioning shift over time</td></tr>
                          <tr><td>🟢/🔴 Bars or Tables</td><td>Visual cues on who’s building pressure</td></tr>
                        </tbody>
                      </table>

                      <p>Typically displayed in a comparative layout per instrument (NIFTY, BANKNIFTY)</p>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>FII Long Futures + Call Longs</strong> → directional play</li>
                        <li><strong>PRO Put Shorts + Client Call Longs</strong> → possible bull trap</li>
                        <li><strong>DII neutral, PRO aggressive</strong> → short-term volatility expected</li>
                        <li>Look at <strong>trend in net positions</strong>, not just 1-day changes</li>
                      </ul>

                      <p>Combine with:</p>
                      <ul>
                        <li><strong>Options Table</strong> for strike alignment</li>
                        <li><strong>Gamma zones</strong> for defense zones</li>
                        <li><strong>Orderflow</strong> to validate execution pressure</li>
                      </ul>

                      <p>💡 <em>Markets are narratives. Participant-wise data shows who’s telling the story — and who’s about to flip the page.</em></p>
                                          
        `
      },

      {
        title: `<h2>FII Data Snapshot – Foreign Money’s Market Mood</h2>`,
        description: `
                      <h3><em>FII Snapshot – See Where the Big Money Is Leaning</em></h3>

                        <p>
                          This section distills the most relevant data from <strong>FII trades</strong> into a
                          sharp, easy-to-read snapshot.
                        </p>

                        <p><strong>Why it matters?</strong></p>
                        <p>
                          FIIs bring size.<br>
                          Size brings direction.<br>
                          And direction, when consistent, shapes the market’s core mood.
                        </p>

                        <p>💡 <em>They don’t chase breakouts. They build positions into noise. Your job? Read the quiet signs before the storm.</em></p>

                        <hr>

                        <h4>🧑‍💼 Where to Access This:</h4>
                        <p>
                          Click <strong>‘FII Data’</strong> under <strong>Premium Access</strong>
                        </p>

                        <hr>

                        <h4>🔍 What You See on the Chart:</h4>
                        <table border="1" cellpadding="6" cellspacing="0">
                          <thead>
                            <tr>
                              <th>📊 Metric</th>
                              <th>Interpretation</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>📈 FII Net Longs in Index Futures</td>
                              <td>Bullish conviction in broader market</td>
                            </tr>
                            <tr>
                              <td>📉 FII Net Shorts in Index Futures</td>
                              <td>Caution or hedge building</td>
                            </tr>
                            <tr>
                              <td>🧲 Net Positions in Options</td>
                              <td>Call/Put ratios reveal directional tilt</td>
                            </tr>
                            <tr>
                              <td>💼 Cash Market Activity</td>
                              <td>Real buying/selling in equities</td>
                            </tr>
                            <tr>
                              <td>🔄 Day-over-Day Delta</td>
                              <td>Detects momentum shift in their strategy</td>
                            </tr>
                          </tbody>
                        </table>

                        <p>Can be paired with visual graphs or raw tables — often shown as a bar chart + net table for the day.</p>

                        <hr>

                        <h4>✅ How to Use It in Trading:</h4>
                        <ul>
                          <li><strong>Rising Futures Longs + Cash Buying</strong> → sustained trend potential</li>
                          <li><strong>Short buildup + Call Selling</strong> → caution or upcoming volatility</li>
                          <li>Watch for <strong>discrepancy</strong>:</li>
                          <ul>
                            <li>If FII data is negative but price is rising → low conviction uptrend</li>
                            <li>If FII data turns positive after consolidation → trend ignition point</li>
                          </ul>
                        </ul>

                        <p>Pair with:</p>
                        <ul>
                          <li><strong>Participant-Wise Table</strong> → confirm behavior overlap</li>
                          <li><strong>Gamma Range</strong> → know where they're defending</li>
                          <li><strong>MP/Orderflow</strong> → validate structure + conviction</li>
                        </ul>

                        <p>💡 <em>You’re not reacting to price. You’re reading positioning. That’s a professional edge.</em></p>
                              `
      }

    ]
  },

  // Free Acess
  {
    title: 'Free Access',
    subs: [
      {
        title: '<h2>Greeks Table (Bridge Table) – Fast Greek Insights at a Glance</h2>',
        description: `
                      <h3><em>Greeks Table – Your Quick Glance at Option Pressure</em></h3>

                      <p>
                        The <strong>Greeks Table</strong> gives you a streamlined view of <strong>Delta, Gamma, Theta, and Vega</strong> 
                        — across key strikes — in one clean snapshot.
                      </p>

                      <p>It’s a <strong>lightweight tool</strong> designed for:</p>
                      <ul>
                        <li>Spotting strike pressure without opening the full Options Table</li>
                        <li>Comparing ATM/NTM strikes for bias</li>
                        <li>Offering new users a soft entry into the power of Greeks</li>
                      </ul>

                      <p>💡 <em>This is your bridge from watching price… to reading pressure.</em></p>

                      <hr>

                      <h4>🧭 Where to Access This:</h4>
                      <ul>
                        <li>Click <strong>‘Greeks’</strong> under <strong>Free Access</strong></li>
                        <li>Choose the instrument (NIFTY, BANKNIFTY, or FINNIFTY)</li>
                      </ul>

                      <hr>

                      <h4>🔍 What You See on the Table:</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>📯 Strike Price</th>
                            <th>📈 Delta</th>
                            <th>🧲 Gamma</th>
                            <th>⏳ Theta</th>
                            <th>🌊 Vega</th>
                            <th>💰 LTP (Call/Put)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Key levels around spot</td>
                            <td>Measures directional sensitivity</td>
                            <td>Rate of Delta change = volatility exposure</td>
                            <td>Premium decay rate</td>
                            <td>IV sensitivity</td>
                            <td>Live option prices</td>
                          </tr>
                        </tbody>
                      </table>
                      <p>🧮 Focused on strikes near spot for rapid assessment</p>

                      <hr>

                      <h4>✅ How to Use It in Trading:</h4>
                      <ul>
                        <li><strong>ATM strike with rising Gamma + low Theta</strong> → price may snap through that level</li>
                        <li><strong>Deep OTM with high Vega</strong> → inflated premium, avoid buying blindly</li>
                        <li><strong>Rising Delta in Calls + rising OI in same strike</strong> → bullish pressure confirmation</li>
                        <li>Use this table to:
                          <ul>
                            <li>Filter down to 2–3 actionable strikes</li>
                            <li>Validate a setup before diving into the full Options Table</li>
                            <li>Get comfortable with Greeks without feeling overwhelmed</li>
                          </ul>
                        </li>
                      </ul>

                      <p>💡 <em>This is where many traders begin to trade smarter, not louder.</em></p>

        
                      `
      },

      {
        title: '<h2>Indices Table – Spot the Pulse of the Market</h2>',
        description: `
                      <h3><em>Indices Table – Your Index Dashboard in Real-Time</em></h3>

                        <p>
                          This tool offers a live snapshot of <strong>NIFTY, BANKNIFTY, and FINNIFTY</strong>, across:
                        </p>

                        <ul>
                          <li>Futures</li>
                          <li>Spot</li>
                          <li>Major components</li>
                        </ul>

                        <p>It’s like having a <strong>real-time market scanner</strong> showing what’s driving the move — without opening multiple charts.</p>

                        <p>💡 <em>Price moves… but internals reveal the intent.</em></p>

                        <hr>

                        <h4>🧭 Where to Access This:</h4>
                        <p>Click <strong>‘Indices’</strong> under <strong>Free Access</strong></p>

                        <hr>

                        <h4>🔍 What You See on the Table:</h4>
                        <table>
                          <thead>
                            <tr>
                              <th>Metric</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>📈 Index Spot</td>
                              <td>Real-time price of NIFTY/BANKNIFTY/FINNIFTY</td>
                            </tr>
                            <tr>
                              <td>💼 Futures Price</td>
                              <td>Derivative contract movement</td>
                            </tr>
                            <tr>
                              <td>🔄 Basis</td>
                              <td>Difference between Spot and Futures (premium or discount)</td>
                            </tr>
                            <tr>
                              <td>📊 Key Components</td>
                              <td>Top movers within the index (e.g., Reliance, HDFC Bank)</td>
                            </tr>
                            <tr>
                              <td>⏱️ % Change</td>
                              <td>Intraday gain/loss snapshot</td>
                            </tr>
                            <tr>
                              <td>🧭 Divergence Column</td>
                              <td>Quickly spot if Spot & Futures are misaligned</td>
                            </tr>
                          </tbody>
                        </table>

                        <p>This layout helps traders assess:</p>
                        <ul>
                          <li>Are futures leading spot (or vice versa)?</li>
                          <li>Is the move index-wide or stock-specific?</li>
                          <li>Are large caps aligned with index direction?</li>
                        </ul>

                        <hr>

                        <h4>✅ How to Use It in Trading:</h4>
                        <ul>
                          <li><strong>Futures rising faster than Spot</strong> → bullish bias may sustain</li>
                          <li><strong>Spot up, Futures flat</strong> → caution, short covering not conviction buying</li>
                          <li><strong>Large weightage stocks not moving</strong> → move may be synthetic</li>
                          <li><strong>Negative divergence in BankNifty vs Nifty</strong> → trend may lack legs</li>
                        </ul>

                        <p>Use it to:</p>
                        <ul>
                          <li>Time entries</li>
                          <li>Avoid false breakouts</li>
                          <li>Align trades with index flow</li>
                        </ul>

                        <p>💡 <em>It’s the heartbeat monitor for the index — use it before chasing a trend.</em></p>

                      `
      },
    ]

  },

  // Bookmarks and Layout
  {
    title: 'Bookmarks and Layout',
    subs: [
      {
        title: '<h2>What Are Bookmarks?</h2>',
        description: `
                      <h3><em>What Are Bookmarks?</em></h3>

                    <p><strong>Bookmarks</strong> let you <strong>save your chart setup exactly the way you want it</strong> — instruments, indicators, layouts, even timeframes.</p>

                    <p>Think of it like saving a preset view of your cockpit:</p>
                    <ul>
                      <li>You come back tomorrow morning</li>
                      <li>Click once</li>
                      <li><strong>Boom</strong> — you're ready to trade from your favorite view</li>
                    </ul>

                    <p>💡 <em>No more reloading symbols, timeframes, or toggling VWAP and IB every time.</em></p>
                      
                      `
      },

      {
        title: '<h2>Global Bookmarks – Save Any Layout</h2>',
        description: `
                      <h3><em>Global Bookmarks – Save Your Multi-Chart Setup</em></h3>

                      <p><strong>Global Bookmarks</strong> let you save <strong>any layout</strong> — across:</p>
                      <ul>
                        <li>Orderflow Charts</li>
                        <li>MarketProfile</li>
                        <li>Smart Candlesticks</li>
                        <li>Options Tools</li>
                        <li>2-panel or multi-panel views</li>
                      </ul>

                      <h4><em>How to Use:</em></h4>
                      <ol>
                        <li>Set up your preferred view — charts, symbols, and tools</li>
                        <li>Click the <strong>‘Global Bookmarks’</strong> icon</li>
                        <li>Give your view a name (e.g., <em>"NIFTY Opening Bias"</em>)</li>
                        <li>Save and access it anytime from the Bookmarks dropdown</li>
                      </ol>

                      <p>💡 <em>You can store multiple layouts for different strategies — Opening Range, Afternoon Fade, Post-News setups, etc.</em></p>

                      
                      `
      },

      {
        title: '<h2>Local Bookmarks – Save Any Chart Setup</h2>',
        description: `
                      <h3><em>MP Bookmarks – For MarketProfile Only</em></h3>

                        <p>If you work deeply with Market Profile, <strong>MP Bookmarks</strong> give you a <strong>quicker
                        way to switch Profile views</strong> without affecting other chart types.</p>

                        <h4>✅ What they save:</h4>
                        <ul>
                          <li>Selected instrument</li>
                          <li>Profile type (daily / weekly / composite)</li>
                          <li>Visual settings like split profile, POC, VA</li>
                        </ul>

                        <h4><em>Use Case Examples:</em></h4>
                        <ul>
                          <li>"NIFTY Daily TPO"</li>
                          <li>"BANKNIFTY Composite + Weekly Bias"</li>
                          <li>"FinNifty VA Break Scanner"</li>
                        </ul>

                        <p><strong>Note:</strong> These are <em>specific to MarketProfile</em> — they don’t store Orderflow
                        or Options data overlays.</p>

                        <p>💡 <em>Think of MP Bookmarks as your custom MP templates.</em></p>
                      `
      },
      {
        title: '<h2>Pro Bookmark Tips & Layout Power Moves</h2>',
        description: `
                      <h3><em>Pro Tips: Using Bookmarks Like a Trading Desk</em></h3>

                        <p>Here’s how our top traders use Bookmarks:</p>

                        <ul>
                          <li>☑️ <strong>Opening View</strong> → Save your go-to premarket bias setup</li>
                          <li>☑️ <strong>Post-Lunch Setup</strong> → Use a different chart layout for afternoon volatility</li>
                          <li>☑️ <strong>Instrument-Specific Views</strong> → Create custom views for BANKNIFTY, FINNIFTY, and stock futures</li>
                        </ul>
                      `
      }
    ],
  },
];

// Icons for each main section (index aligned)
const SECTION_ICONS = [
  <HomeOutlinedIcon sx={{ color: '#fff' }} />, // Start Here
  <MenuBookOutlinedIcon sx={{ color: '#fff' }} />, // How to Use Vtrender Charts
  <FlashOnOutlinedIcon sx={{ color: '#fff' }} />, // Quick Start
  <AccountTreeOutlinedIcon sx={{ color: '#fff' }} />, // OrderFlow
  <ShowChartIcon sx={{ color: '#fff' }} />, // Explore Marketprofile
  <CandlestickChartOutlinedIcon sx={{ color: '#fff' }} />, // Smart Candlesticks
  <WaterfallChartOutlinedIcon sx={{ color: '#fff' }} />, // Explore Options
  <PremiumAccessIcon sx={{ color: '#fff' }} />, // Premium Access
  <FiberNewIcon sx={{ color: '#fff' }} />, // Free Access
  <BookmarksOutlinedIcon sx={{ color: '#fff' }} />, // Bookmarks and Layout
];

// Placeholder card content for right pane
const ManualAccordion = ({ title, description }) => {
  return (
    <Accordion 
      sx={{ 
        backgroundColor: '#183669ff', 
        color: '#fff', 
        mb: 2.5, 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', 
        borderRadius: '8px !important',
        overflow: 'hidden',
        border: '1px solid rgba(63, 105, 248, 0.15)',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 0 20px 0',
          borderLeft: '4px solid #3F69F8',
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#1132a0ff', fontSize: '1.4rem' }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          minHeight: '48px',
          '&.Mui-expanded': {
            backgroundColor: 'rgba(17, 95, 98, 0.51)',
            minHeight: '48px',
          },
          '& .MuiAccordionSummary-content': {
            margin: '2px 0',
            '&.Mui-expanded': {
              margin: '2px 0',
            }
          }
        }}
      >
        <div 
          dangerouslySetInnerHTML={{ __html: title }} 
          style={{ 
            fontWeight: 500,
            fontSize: '1.05rem',
          }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{
        padding: '16px 24px 24px',
        borderTop: '1px solid rgba(63, 105, 248, 0.1)',
        '& h2': {
          fontSize: '1.2rem',
          fontWeight: 500,
          marginBottom: '16px',
          color: '#fff',
        },

        '& p': {
          marginBottom: '16px',
          lineHeight: 1.6,
          fontSize: '0.95rem',
        },
        '& ul, & ol': {
          paddingLeft: '24px',
          marginBottom: '16px',
          marginTop: '8px',
        },
        '& li': {
          marginBottom: '10px',
          lineHeight: 1.5,
          fontSize: '0.95rem',
          paddingLeft: '4px',
        },
        '& li ul, & li ol': {
          marginTop: '10px',
          marginBottom: '10px',
        },
        '& li li': {
          marginBottom: '6px',
        },
        '& h3': {
          marginTop: '24px',
          fontSize: '1rem',
          fontWeight: 500,
          marginBottom: '12px',
          color: '#e0e6f5',
        },
        '& h4': {
          marginTop: '20px',
          marginLeft: '12px',
          paddingLeft: '8px',
          borderLeft: '2px solid rgba(63, 105, 248, 0.5)',
        },
        '& hr + h4': {
          marginTop: '16px',
        },
        '& h4 + p, & h4 + ul, & h4 + ol': {
          marginLeft: '12px',
          paddingLeft: '8px',
        },
        '& strong': {
          color: '#4deeea',
          fontWeight: 600,
        },
        '& em': {
          color: '#e0e6f5',
          fontStyle: 'italic',
        },
        '& .tip': {
          backgroundColor: 'rgba(63, 105, 248, 0.1)',
          borderLeft: '4px solid #3F69F8',
          padding: '12px 16px',
          borderRadius: '4px',
          marginTop: '16px',
          marginBottom: '16px',
          '& strong': {
            color: '#ff9d4d',
            fontWeight: 600,
          },
        },
        '& .signal': {
          backgroundColor: 'rgba(0, 200, 83, 0.1)',
          borderLeft: '4px solid #00c853',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '8px',
        },
        '& img': {
          maxWidth: '100%',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          margin: '16px 0',
          display: 'block',
          height: 'auto',
          '@media (min-width: 600px)': {
            maxWidth: '80%',
            marginLeft: 'auto',
            marginRight: 'auto',
          },
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '20px',
          backgroundColor: 'rgba(19, 45, 90, 0.5)',
          border: '2px solid #3F69F8',
          overflow: 'hidden',
        },
        '& th': {
          backgroundColor: 'rgba(63, 105, 248, 0.2)',
          color: '#4deeea',
          padding: '12px 8px',
          textAlign: 'left',
          fontWeight: 600,
          borderBottom: '2px solid #3F69F8',
          borderRight: '2px solid #3F69F8',
          fontSize: '0.9rem',
        },
        '& td': {
          padding: '10px 8px',
          borderBottom: '2px solid #3F69F8',
          borderRight: '2px solid #3F69F8',
          fontSize: '0.9rem',
          color: '#e0e6f5',
        },
        '& tr': {
          borderBottom: '2px solid #3F69F8',
        },
        '& tr:last-child': {
          borderBottom: 'none',
        },
        '& th:last-child, & td:last-child': {
          borderRight: '2px solid #3F69F8',
        },
      }}>
        <div dangerouslySetInnerHTML={{ __html: description }} />
      </AccordionDetails>
    </Accordion>
  );
};

const UserManualNew = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const isDesktop = useMediaQuery('(min-width:1024px)');

  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
      setCollapsed(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  const handleListItemClick = (index) => {
    setSelectedIndex(index);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* Left Drawer with Menu */}
      <Box
        sx={{
          width: drawerOpen ? (isMobile ? '100%' : (collapsed && isDesktop ? '70px' : '280px')) : '0px',
          flexShrink: 0,
          transition: 'width 0.2s',
          overflow: 'hidden',
          height: '100%',
          position: isMobile ? 'absolute' : 'relative',
          zIndex: isMobile ? 1200 : 'auto',
          backgroundColor: '#0d2147',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!collapsed && (
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              User Manual
            </Typography>
          )}
          {isMobile ? (
            <IconButton onClick={toggleDrawer} sx={{ color: '#fff' }}>
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <IconButton onClick={toggleCollapse} sx={{ color: '#fff' }}>
              {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </Box>
        <List sx={{ width: '100%', pt: 0 }}>
          {MANUAL_SECTIONS.map((section, index) => (
            <React.Fragment key={`section-${index}`}>
              <ListItemButton
                selected={selectedIndex === index}
                onClick={() => handleListItemClick(index)}
                sx={{
                  py: 1.5,
                  backgroundColor: selectedIndex === index ? '#3F69F8' : 'transparent',
                  '&:hover': {
                    backgroundColor: selectedIndex === index ? '#3F69F8' : 'rgba(63, 105, 248, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 40, mr: collapsed ? 0 : 2, justifyContent: collapsed ? 'center' : 'flex-start' }}>
                  {SECTION_ICONS[index]}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={section.title}
                    primaryTypographyProps={{
                      sx: {
                        color: '#fff',
                        fontWeight: selectedIndex === index ? 'bold' : 'normal',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'auto',
          backgroundColor: '#132d5a',
          position: 'relative',
          p: { xs: 2, sm: 3, md: 4 },
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#3F69F8', borderRadius: 4 },
        }}
      >
        {/* Mobile Menu Toggle Button */}
        {isMobile && !drawerOpen && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 1100,
              color: '#fff',
              backgroundColor: '#3F69F8',
              '&:hover': { backgroundColor: '#3357d6' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Section Title and Header */}
        <Paper 
          elevation={0}
          sx={{
            mb: 4, 
            p: 3,
            backgroundColor: 'rgba(110, 139, 233, 0.08)',
            borderRadius: '12px',
            borderLeft: '4px solid #3F69F8',
            mt: isMobile && !drawerOpen ? 5 : 0,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', md: '1.8rem' },
              mb: 1,
            }}
          >
            {MANUAL_SECTIONS[selectedIndex].title}
          </Typography>
          <Typography 
            variant="body1"
            sx={{
              color: '#e0e6f5',
              fontSize: '0.95rem',
            }}
          >
            Explore everything you need to know about {MANUAL_SECTIONS[selectedIndex].title.toLowerCase()}
          </Typography>
        </Paper>

        {/* Section Content */}
        <Box sx={{ maxWidth: '80%', mx: 'auto' }}>
          {MANUAL_SECTIONS[selectedIndex].subs.map((sub, idx) => (
            <ManualAccordion key={`sub-${idx}`} title={sub.title} description={sub.description} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default UserManualNew;
